import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import T2TTopNav from "../Layout/T2TTopNav";
import { getData, postData } from "../../../FetchApiServices";
import { useToast } from "../Layout/T2TUIProvider";

// Integration type labels + icons
const INT_META = {
  sap_s4hana:    { label: "SAP S/4HANA",     icon: "bi bi-box-seam-fill",   color: "#0070F2" },
  sap_fiori_sso: { label: "SAP Fiori SSO",    icon: "bi bi-shield-lock-fill", color: "#1B3A6B" },
  sap_ariba:     { label: "SAP Ariba",         icon: "bi bi-cart-fill",       color: "#F0AB00" },
  ariba_network: { label: "Ariba Network",     icon: "bi bi-globe2",          color: "#D97706" },
  wati_whatsapp: { label: "WhatsApp (WATI)",   icon: "bi bi-whatsapp",        color: "#25D366" },
  pan_verify:    { label: "PAN Verification",  icon: "bi bi-person-vcard",    color: "#7C3AED" },
  gst_verify:    { label: "GST Verification",  icon: "bi bi-receipt",         color: "#0F766E" },
  bank_verify:   { label: "Bank Verification", icon: "bi bi-bank2",           color: "#2563EB" },
  gstn_einvoice: { label: "GSTN e-Invoice",    icon: "bi bi-file-earmark-check", color: "#DC2626" },
  gem_portal:    { label: "GeM Portal",        icon: "bi bi-shop",            color: "#16A34A" },
  treds_rxil:    { label: "TReDS (RXIL)",      icon: "bi bi-cash-stack",      color: "#92400E" },
};

const STATUS_COLORS = {
  healthy:      "#16A34A",
  degraded:     "#D97706",
  down:         "#DC2626",
  unconfigured: "#94A3B8",
};

const T2TIntegrations = () => {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState("health");

  return (
    <>
      <T2TTopNav title="Integration Monitor" />
      <div className="t2t-content">
        <div className="t2t-page-title">Integration Monitor</div>
        <p className="t2t-page-sub">Health status, API call logs, and cost tracking for all integrations.</p>

        <div className="t2t-tab-group" style={{ marginBottom: 20 }}>
          <button className={`t2t-tab${activeTab === "health" ? " active" : ""}`} onClick={() => setActiveTab("health")}>Health Status</button>
          <button className={`t2t-tab${activeTab === "logs" ? " active" : ""}`} onClick={() => setActiveTab("logs")}>Call Log</button>
          <button className={`t2t-tab${activeTab === "costs" ? " active" : ""}`} onClick={() => setActiveTab("costs")}>Cost Tracker</button>
        </div>

        {activeTab === "health" && <HealthTab />}
        {activeTab === "logs" && <CallLogTab toast={toast} />}
        {activeTab === "costs" && <CostTab />}
      </div>
    </>
  );
};

// ═══════════════════════════════════════════════════════════════════
// TAB 1 — Health Status Cards
// ═══════════════════════════════════════════════════════════════════
const HealthTab = () => {
  const [health, setHealth] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchHealth(); }, []);

  const fetchHealth = async () => {
    setLoading(true);
    const resp = await getData("t2t/integrations/health");
    if (!resp.error) {
      const raw = resp.data?.data;
      setHealth(Array.isArray(raw) ? raw : []);
    }
    setLoading(false);
  };

  if (loading) return <div style={{ padding: 32, textAlign: "center", color: "var(--t2t-text-muted)", fontSize: 12 }}>Loading...</div>;

  // Always show all known types; merge API data for configured ones
  const items = Object.keys(INT_META).map((type) => {
    const found = health.find((h) => h.integration_type === type);
    return found || { integration_type: type, status: "unconfigured", response_ms: null, last_checked: null };
  });

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 14 }}>
      {items.map((h) => {
        const meta = INT_META[h.integration_type] || { label: h.integration_type, icon: "bi bi-plug-fill", color: "#94A3B8" };
        const statusColor = STATUS_COLORS[h.status] || "#94A3B8";
        return (
          <div key={h.integration_type} style={{ background: "var(--t2t-surface)", border: "1px solid var(--t2t-border)", borderRadius: 10, padding: 18, display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: meta.color + "18", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className={meta.icon} style={{ fontSize: 16, color: meta.color }}></i>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{meta.label}</div>
              </div>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: statusColor, flexShrink: 0 }} title={h.status}></div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--t2t-text-secondary)" }}>
              <span style={{ fontWeight: 700, color: statusColor, textTransform: "uppercase", fontSize: 10, letterSpacing: ".04em" }}>{h.status}</span>
              {h.response_ms != null && <span>{h.response_ms}ms</span>}
            </div>
            {h.last_checked && (
              <div style={{ fontSize: 10, color: "#94A3B8" }}>
                Last checked: {new Date(h.last_checked).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
              </div>
            )}
            {h.error_message && (
              <div style={{ fontSize: 10, color: "#DC2626", background: "#FEF2F2", padding: "4px 8px", borderRadius: 4, lineHeight: 1.4 }}>{h.error_message}</div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// TAB 2 — Call Log Table
// ═══════════════════════════════════════════════════════════════════
const CallLogTab = ({ toast }) => {
  const [logs, setLogs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ type: "", status: "", date_from: "", date_to: "" });
  const [retrying, setRetrying] = useState(null);

  useEffect(() => { fetchLogs(); }, [filters]);

  const fetchLogs = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.type)      params.set("type", filters.type);
    if (filters.status)    params.set("status", filters.status);
    if (filters.date_from) params.set("date_from", filters.date_from);
    if (filters.date_to)   params.set("date_to", filters.date_to);
    params.set("limit", "50");
    const resp = await getData(`t2t/integrations/call-log?${params.toString()}`);
    if (!resp.error) {
      const raw = resp.data?.data;
      setLogs(Array.isArray(raw) ? raw : []);
    }
    setLoading(false);
  };

  const retryCall = async (logId) => {
    setRetrying(logId);
    const resp = await postData(`t2t/integrations/call-log/${logId}/retry`, {});
    if (resp.error) { toast(resp.error.message || "Retry failed.", "error"); }
    else { toast("Retry triggered.", "success"); fetchLogs(); }
    setRetrying(null);
  };

  const LOG_STATUS_COLORS = { success: "#16A34A", failed: "#DC2626", timeout: "#D97706", retrying: "#2563EB" };

  return (
    <>
      {/* Filters */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <select className="t2t-fc" style={{ flex: 1, minWidth: 140 }} value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}>
          <option value="">All Integrations</option>
          {Object.entries(INT_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select className="t2t-fc" style={{ flex: 1, minWidth: 120 }} value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
          <option value="">All Status</option>
          <option value="success">Success</option>
          <option value="failed">Failed</option>
          <option value="timeout">Timeout</option>
          <option value="retrying">Retrying</option>
        </select>
        <input className="t2t-fc" type="date" style={{ flex: 1, minWidth: 130 }} value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} title="From" />
        <input className="t2t-fc" type="date" style={{ flex: 1, minWidth: 130 }} value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} title="To" />
      </div>

      {loading ? (
        <div style={{ padding: 32, textAlign: "center", color: "var(--t2t-text-muted)", fontSize: 12 }}>Loading...</div>
      ) : logs.length === 0 ? (
        <div className="t2t-empty-state">
          <div className="t2t-empty-icon">📋</div>
          <div className="t2t-empty-title">No call logs</div>
          <div className="t2t-empty-desc">Integration call history will appear here.</div>
        </div>
      ) : (
        <div className="t2t-table">
          <div className="t2t-table-head" style={{ gridTemplateColumns: "130px 1fr 1fr 100px 80px 70px 1fr 60px" }}>
            <div>Timestamp</div>
            <div>Integration</div>
            <div>Action</div>
            <div>Request</div>
            <div>Status</div>
            <div>Duration</div>
            <div>Error</div>
            <div>Retry</div>
          </div>
          {logs.map((log) => {
            const meta = INT_META[log.integration_type] || { label: log.integration_type, color: "#94A3B8" };
            const sColor = LOG_STATUS_COLORS[log.status] || "#94A3B8";
            return (
              <div className="t2t-table-row" key={log.id} style={{ gridTemplateColumns: "130px 1fr 1fr 100px 80px 70px 1fr 60px", cursor: "default" }}>
                <div style={{ fontSize: 10, color: "#64748B" }}>
                  {log.timestamp ? new Date(log.timestamp).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—"}
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: meta.color }}>{meta.label}</div>
                <div style={{ fontSize: 11, color: "var(--t2t-text-secondary)" }}>{log.action || "—"}</div>
                <div style={{ fontSize: 11, color: "var(--t2t-primary)", fontWeight: 600 }}>{log.request_ref || "—"}</div>
                <div>
                  <span style={{ fontSize: 10, fontWeight: 700, color: sColor, background: sColor + "18", padding: "2px 8px", borderRadius: 10, textTransform: "uppercase" }}>
                    {log.status}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: "#64748B" }}>{log.duration_ms != null ? `${log.duration_ms}ms` : "—"}</div>
                <div style={{ fontSize: 10, color: "#DC2626", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={log.error_detail || ""}>
                  {log.error_detail || "—"}
                </div>
                <div>
                  {log.status === "failed" && (
                    <button
                      className="t2t-btn t2t-btn-ghost t2t-btn-sm"
                      style={{ fontSize: 10, padding: "2px 6px" }}
                      onClick={() => retryCall(log.id)}
                      disabled={retrying === log.id}
                    >
                      {retrying === log.id ? "..." : "Retry"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

// ═══════════════════════════════════════════════════════════════════
// TAB 3 — Cost Tracker
// ═══════════════════════════════════════════════════════════════════
const CostTab = () => {
  const [costs, setCosts]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth]     = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });

  useEffect(() => { fetchCosts(); }, [month]);

  const fetchCosts = async () => {
    setLoading(true);
    const resp = await getData(`t2t/integrations/cost-tracker?month=${month}`);
    if (!resp.error) {
      const raw = resp.data?.data;
      setCosts(Array.isArray(raw) ? raw : []);
    }
    setLoading(false);
  };

  const totalCost = costs.reduce((sum, c) => sum + (Number(c.total_cost_rupees) || 0), 0);
  const totalCalls = costs.reduce((sum, c) => sum + (Number(c.total_calls) || 0), 0);

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <input className="t2t-fc" type="month" value={month} onChange={(e) => setMonth(e.target.value)} style={{ width: 180 }} />
        <div style={{ fontSize: 12, color: "var(--t2t-text-secondary)" }}>
          Total: <strong style={{ color: "var(--t2t-text-primary)" }}>{totalCalls} calls</strong> &middot; <strong style={{ color: "#DC2626" }}>{"\u20B9"}{totalCost.toFixed(2)}</strong>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 32, textAlign: "center", color: "var(--t2t-text-muted)", fontSize: 12 }}>Loading...</div>
      ) : costs.length === 0 ? (
        <div className="t2t-empty-state">
          <div className="t2t-empty-icon">💰</div>
          <div className="t2t-empty-title">No cost data</div>
          <div className="t2t-empty-desc">Verification API usage and costs will appear here.</div>
        </div>
      ) : (
        <div className="t2t-table">
          <div className="t2t-table-head" style={{ gridTemplateColumns: "1fr 1fr 100px 100px 120px 100px" }}>
            <div>Verification Type</div>
            <div>Provider</div>
            <div>Total Calls</div>
            <div>Successful</div>
            <div>Total Cost</div>
            <div>Avg / Call</div>
          </div>
          {costs.map((c, i) => (
            <div className="t2t-table-row" key={i} style={{ gridTemplateColumns: "1fr 1fr 100px 100px 120px 100px", cursor: "default" }}>
              <div style={{ fontWeight: 600, fontSize: 12 }}>{c.verification_type || "—"}</div>
              <div style={{ fontSize: 12, color: "var(--t2t-text-secondary)" }}>{c.api_provider || "—"}</div>
              <div style={{ fontWeight: 700, color: "#2563EB" }}>{c.total_calls || 0}</div>
              <div style={{ color: "#16A34A", fontWeight: 600 }}>{c.successful_calls || 0}</div>
              <div style={{ fontWeight: 700, color: "#DC2626" }}>{"\u20B9"}{Number(c.total_cost_rupees || 0).toFixed(2)}</div>
              <div style={{ fontSize: 11, color: "#64748B" }}>{"\u20B9"}{Number(c.avg_cost_per_call || 0).toFixed(2)}</div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

CallLogTab.propTypes = { toast: PropTypes.func.isRequired };

export default T2TIntegrations;
